import { useRouter } from "next/router";
import { useCallback, useMemo } from "react";

import { APIOps } from "@/apis/api-config";
import CorporateApi, { CorpMediaV2QueryParams, OrganizationRole } from "@/apis/connect/corporate-api";
import {
  CORPORATE_DASHBOARD_PATH,
  CORPORATE_DEMO_PATH,
  CORPORATE_INVITE_PATH,
  CORPORATE_LOGIN_PATH,
} from "@/global-state/external-path";
import { firebaseAuth } from "@/global-state/firebase-settings";
import {
  authUserAtom,
  corpMembershipsAtom,
  corporateDemoAtom,
  idTokenAtom,
  instagramAccountInfoAtom,
  loadingAtom,
  organizationAtom,
  selectedOrganizationIdAtom,
} from "@/global-state/jotai-atoms";
import { LocalForageKeys } from "@/global-state/local-forage-keys";

import {
  CORPORATE_DEMO_EMAIL,
  endCorporateDemoSession,
  isCorporateDemoSession,
  startCorporateDemoSession,
} from "@/constants/corporate-demo";
import {
  clearSelectedOrganizationId,
  readSelectedOrganizationId,
  resolveSelectedOrganizationId,
  writeSelectedOrganizationId,
} from "@/constants/corporate-organization";
import {
  SAMPLE_CORPORATE_ACCOUNTS,
  SAMPLE_CORPORATE_ACCOUNT_LIST_RESPONSE,
  SAMPLE_CORPORATE_FEED_ITEMS,
  SAMPLE_CORPORATE_INSIGHT,
  SAMPLE_CORPORATE_INSTAGRAM_ACCOUNT,
  SAMPLE_CORPORATE_INVITATIONS,
  SAMPLE_CORPORATE_MEMBERS,
  SAMPLE_CORPORATE_MEMBERSHIPS,
  SAMPLE_CORPORATE_ORGANIZATION,
  SAMPLE_CORPORATE_ORG_INSTAGRAM_ACCOUNTS,
  SAMPLE_CORPORATE_REEL_ITEMS,
  SAMPLE_CORPORATE_STORY_ITEMS,
  SAMPLE_CORPORATE_TOP_WORST_MEDIA,
  sampleCorporateMember,
} from "@/constants/sample-data/sample-corporate";
import { signInWithCustomToken, signInWithEmailAndPassword } from "firebase/auth";
import { useAtom } from "jotai";
import localforage from "localforage";
import { useSnackbar } from "notistack";

/** デモアカウントでのログインもデモ扱いにする（URL経由が主な導線） */
const isCorporateDemoEmail = (email?: string | null): boolean =>
  !!email && email.trim().toLowerCase() === CORPORATE_DEMO_EMAIL.toLowerCase();

export const useCorporateApi = () => {
  const router = useRouter();
  const [, setLoading] = useAtom(loadingAtom);
  const [instagram, setInstagram] = useAtom(instagramAccountInfoAtom);
  const [organization] = useAtom(organizationAtom);
  const [, setMemberships] = useAtom(corpMembershipsAtom);
  const [, setSelectedOrganizationId] = useAtom(selectedOrganizationIdAtom);
  const [idToken, setIdToken] = useAtom(idTokenAtom);
  const [, setAuthUser] = useAtom(authUserAtom);
  const [isDemo, setIsDemo] = useAtom(corporateDemoAtom);
  const coporateApi = useMemo(
    () => new CorporateApi(idToken, instagram?.instagram_business_id, organization?.id),
    [idToken, instagram, organization?.id],
  );
  const { enqueueSnackbar } = useSnackbar();

  /** 招待の受諾ページは未ログインで開く。ここでログイン画面へ飛ばさない */
  const onInviteEntry = () =>
    typeof window !== "undefined" && window.location.pathname.startsWith(CORPORATE_INVITE_PATH);

  /**
   * ログイン画面より内側の法人ページを開いているか。
   * リロードや直リンクで戻ってきたときに、開いていたページから動かさないための判定。
   */
  const onCorporateInnerPage = () =>
    typeof window !== "undefined" &&
    window.location.pathname.startsWith(`${CORPORATE_LOGIN_PATH}/`) &&
    !window.location.pathname.startsWith(CORPORATE_INVITE_PATH);

  // id, 認証情報リセット => error, logout時に使用
  const resetAuth = useCallback(async () => {
    setAuthUser(null);
    setIdToken(null);
    setIsDemo(false);
    setMemberships([]);
    setSelectedOrganizationId(null);
    endCorporateDemoSession();
    setLoading(false);
    if (onInviteEntry()) return;
    await router.push(CORPORATE_LOGIN_PATH);
  }, []);

  /**
   * ログイン後の状態を組み立てる（impersonate / 自動ログイン / フォームログイン 共通）
   *
   * Instagram 未連携の閲覧専用メンバーでも入れるよう、instagram は null 許容にする。
   * 所属が 0 件のときは例外にせず空のまま返し、画面側で案内を出す。
   */
  const bootstrapCorporateSession = useCallback(
    async (accessToken: string, preferredOrganizationId?: string | null) => {
      const api = new CorporateApi(accessToken);
      const member = await api.postCoroporateLogin();
      const memberships = member.organizations || [];
      setIsDemo(false);
      setAuthUser(member);
      setInstagram(member.instagram_accounts?.[0] ?? null);
      setMemberships(memberships);
      const orgId = resolveSelectedOrganizationId(
        memberships,
        readSelectedOrganizationId(member.firebase_auth_user_id),
        preferredOrganizationId,
      );
      setSelectedOrganizationId(orgId);
      writeSelectedOrganizationId(member.firebase_auth_user_id, orgId);
      return member;
    },
    [],
  );

  /**
   * デモセッションを組み立てる
   * Firebase 認証さえ通っていれば入れるようにし、DB への組織・ユーザー登録を不要にする
   */
  const applyDemoSession = useCallback((options?: { silent?: boolean }) => {
    startCorporateDemoSession();
    setIsDemo(true);
    setAuthUser(sampleCorporateMember(CORPORATE_DEMO_EMAIL));
    setInstagram(SAMPLE_CORPORATE_INSTAGRAM_ACCOUNT);
    setMemberships(SAMPLE_CORPORATE_MEMBERSHIPS);
    setSelectedOrganizationId(SAMPLE_CORPORATE_ORGANIZATION.id);
    setLoading(false);
    if (!options?.silent) {
      enqueueSnackbar("デモ環境（サンプルデータ）を表示しています", { variant: "info" });
    }
  }, []);

  const corporateAuthSync = useCallback(() => {
    // URL から入ったデモは Firebase 認証を経由しない。
    // APP_ENV=corporate ではこの同期が先に走ってログイン画面へ飛ばすため、
    // デモの印か /corporate/demo への直接アクセスを先に拾う
    const onDemoEntry = typeof window !== "undefined" && window.location.pathname.startsWith(CORPORATE_DEMO_PATH);
    if (isCorporateDemoSession() || onDemoEntry) {
      applyDemoSession({ silent: isCorporateDemoSession() && !onDemoEntry });
      return;
    }

    setLoading(true);

    // URLパラメータから認証トークンを取得
    const params = new URLSearchParams(window.location.search);
    const impersonateToken = params.get("impersonate_token");

    // 管理者なりすましトークンがある場合
    if (impersonateToken) {
      console.log("🔍 Debug - impersonateToken found:", impersonateToken);

      // URLパラメータをクリア（セキュリティのため）
      window.history.replaceState({}, document.title, window.location.pathname);

      // 認証トークンを検証してFirebaseカスタムトークンを取得
      const verifyApi = new CorporateApi();
      console.log("🔍 Debug - Calling postVerifyImpersonateToken...");

      verifyApi
        .postVerifyImpersonateToken(impersonateToken)
        .then(async (response) => {
          console.log("🔍 Debug - Token verified successfully:", response);

          // Firebaseカスタムトークンで認証
          console.log("🔍 Debug - Signing in with custom token...");
          const userCredential = await signInWithCustomToken(firebaseAuth, response.firebase_custom_token);

          console.log("🔍 Debug - Firebase authentication successful:", userCredential.user.uid);

          // Firebase JWTを取得
          const accessToken = await userCredential.user.getIdToken();
          console.log("🔍 Debug - Firebase JWT obtained");

          // JWTを保存
          await setIdToken(accessToken);

          // 管理者が指定した組織を選択状態にする
          await bootstrapCorporateSession(accessToken, response.organization_id);

          enqueueSnackbar("管理者として組織ダッシュボードにアクセス中です", {
            variant: "info",
          });

          await router.push(CORPORATE_DASHBOARD_PATH);
          setLoading(false);
        })
        .catch((error) => {
          console.error("🔍 Debug - Token verification or authentication failed:", error);
          console.error("🔍 Debug - Error response:", error?.response);
          console.error("🔍 Debug - Error detail:", error?.response?.data?.detail);

          enqueueSnackbar(error?.response?.data?.detail || error?.message || "認証に失敗しました", {
            variant: "error",
          });
          setLoading(false);
          resetAuth();
        });

      return;
    }

    // 通常のログイン処理（既存ロジック）
    try {
      firebaseAuth.onAuthStateChanged(async (user) => {
        if (user) {
          const accessToken = await user.getIdToken();
          await setIdToken(accessToken);
          // 招待の受諾ページでは、ログイン状態でも受諾が終わるまで通常のログインフローを走らせない
          // (判定はコールバックの実行時に行う。登録時のパスでは足りない)
          if (onInviteEntry()) {
            setLoading(false);
            return;
          }
          // デモアカウントは実データを一切参照しないため、組織・ユーザーのDB登録を必要としない
          if (isCorporateDemoEmail(user.email)) {
            applyDemoSession();
            await router.push(CORPORATE_DASHBOARD_PATH);
            return;
          }
          try {
            await bootstrapCorporateSession(accessToken);
            enqueueSnackbar("管理者ダッシュボードにログイン中です", {
              variant: "success",
            });
            // サマリーへ送るのはログイン画面から入ってきたときだけ。
            // 開いていたページでリロードしたときにサマリーへ引き戻さない。
            if (!onCorporateInnerPage()) await router.push(CORPORATE_DASHBOARD_PATH);
            setLoading(false);
          } catch (e) {
            enqueueSnackbar(e?.response?.data?.detail || e?.message, {
              variant: "error",
            });
            setIdToken("");
            setLoading(false);
            return;
          }
        } else {
          setLoading(false);
          // ログイン画面で張ったこのリスナーは、デモへ移ったあとも生き続ける。
          // デモは Firebase 認証を通らないので、ここで未ログインとして扱うと
          // applyDemoSession が入れたサンプルの組織まで巻き添えで消えてしまう
          if (isCorporateDemoSession()) return;
          return resetAuth();
        }
      });
    } catch (e) {
      enqueueSnackbar(e?.response?.data?.detail || e?.message, {
        variant: "error",
      });
      setLoading(false);
    }
  }, [coporateApi, isDemo]);

  const postCoroporateLogin = useCallback(
    async (email: string, password: string) => {
      try {
        signInWithEmailAndPassword(firebaseAuth, email, password)
          .then(async (cred) => {
            setLoading(true);
            await localforage.setItem(LocalForageKeys.SigninForm.email, email);
            await localforage.setItem(LocalForageKeys.SigninForm.password, password);
            const linkUser = cred.user;
            const accessToken = (await linkUser.getIdToken()) || null;
            await setIdToken(accessToken);
            if (isCorporateDemoEmail(email)) {
              applyDemoSession();
              await router.push(CORPORATE_DASHBOARD_PATH);
              return;
            }
            // 同じタブでデモを見たあと実アカウントに入り直す動線がある。
            // デモの印が残っていると通信そのものが止まるので、実ログインの前に必ず落とす
            endCorporateDemoSession();
            setIsDemo(false);
            try {
              await bootstrapCorporateSession(accessToken);
              await router.push(CORPORATE_DASHBOARD_PATH);
            } catch (e) {
              enqueueSnackbar(e?.response?.data?.detail || e?.message, {
                variant: "error",
              });
              return setIdToken("");
            }
            enqueueSnackbar(`${email + "で" || ""}管理者ダッシュボードにログイン中です`, {
              variant: "success",
            });
          })
          .catch((error) => {
            const code = error.code;
            if (code === "auth/user-not-found") {
              enqueueSnackbar("未登録のユーザーです。新規登録をお願いします。", {
                variant: "error",
              });
            } else if (code === "auth/wrong-password") {
              enqueueSnackbar("パスワードが一致しませんでした。", {
                variant: "error",
              });
            } else {
              enqueueSnackbar(error?.response?.data?.detail || error?.message, {
                variant: "error",
              });
            }
            return setIdToken("");
          })
          .finally(() => {
            setLoading(false);
          });
      } catch (e) {
        enqueueSnackbar(e?.response?.data?.detail || e?.message, {
          variant: "error",
        });
      } finally {
        setLoading(false);
      }
    },
    [coporateApi, isDemo],
  );

  const corporateLogout = useCallback(async () => {
    setLoading(true);
    clearSelectedOrganizationId(firebaseAuth.currentUser?.uid);
    try {
      firebaseAuth
        .signOut()
        .then(async () => {
          enqueueSnackbar("ログアウトしました。", {
            variant: "info",
          });
          await router.push(CORPORATE_LOGIN_PATH);
        })
        .catch((error) => {
          enqueueSnackbar(error?.message || "error", {
            variant: "error",
          });
        });
    } catch (e) {
      console.log(e, e?.response?.data?.detail);
      return resetAuth();
    } finally {
      await resetAuth();
      setLoading(false);
    }
  }, [coporateApi, isDemo]);

  // ---- 所属組織・メンバー・招待・IG 割当 ----

  const getMyOrganizations = useCallback(async () => {
    if (isDemo) return SAMPLE_CORPORATE_MEMBERSHIPS;
    try {
      return await coporateApi.getMyOrganizations();
    } catch (e) {
      enqueueSnackbar(e?.response?.data?.detail || e?.message, { variant: "error" });
      return [];
    }
  }, [coporateApi, isDemo]);

  const getOrganizationMembers = useCallback(async () => {
    if (isDemo) return SAMPLE_CORPORATE_MEMBERS;
    try {
      return await coporateApi.getOrganizationMembers();
    } catch (e) {
      enqueueSnackbar(e?.response?.data?.detail || e?.message, { variant: "error" });
      return [];
    }
  }, [coporateApi, isDemo]);

  const patchOrganizationMemberRole = useCallback(
    async (firebaseUserId: string, role: OrganizationRole) => {
      if (isDemo) {
        enqueueSnackbar("デモ環境では変更できません", { variant: "info" });
        return;
      }
      try {
        const result = await coporateApi.patchOrganizationMemberRole(firebaseUserId, role);
        enqueueSnackbar("役割を変更しました", { variant: "success" });
        return result;
      } catch (e) {
        enqueueSnackbar(e?.response?.data?.detail || e?.message, { variant: "error" });
        throw e;
      }
    },
    [coporateApi, isDemo],
  );

  const removeOrganizationMembers = useCallback(
    async (firebaseUserIds: string[]) => {
      if (isDemo) {
        enqueueSnackbar("デモ環境では変更できません", { variant: "info" });
        return;
      }
      try {
        const result = await coporateApi.postRemoveOrganizationMembers(firebaseUserIds);
        enqueueSnackbar(`${result.removed}件のメンバーを削除しました`, { variant: "success" });
        return result;
      } catch (e) {
        enqueueSnackbar(e?.response?.data?.detail || e?.message, { variant: "error" });
        throw e;
      }
    },
    [coporateApi, isDemo],
  );

  const postTransferOwner = useCallback(
    async (firebaseUserId: string, myNewRole: OrganizationRole) => {
      if (isDemo) {
        enqueueSnackbar("デモ環境では変更できません", { variant: "info" });
        return;
      }
      try {
        const result = await coporateApi.postTransferOwner(firebaseUserId, myNewRole);
        enqueueSnackbar("オーナーを移譲しました", { variant: "success" });
        return result;
      } catch (e) {
        enqueueSnackbar(e?.response?.data?.detail || e?.message, { variant: "error" });
        throw e;
      }
    },
    [coporateApi, isDemo],
  );

  const getOrganizationInvitations = useCallback(async () => {
    if (isDemo) return SAMPLE_CORPORATE_INVITATIONS;
    try {
      return await coporateApi.getOrganizationInvitations();
    } catch (e) {
      enqueueSnackbar(e?.response?.data?.detail || e?.message, { variant: "error" });
      return [];
    }
  }, [coporateApi, isDemo]);

  const postOrganizationInvitation = useCallback(
    async (email: string, role: OrganizationRole) => {
      if (isDemo) {
        enqueueSnackbar("デモ環境では変更できません", { variant: "info" });
        return;
      }
      try {
        const result = await coporateApi.postOrganizationInvitation(email, role);
        enqueueSnackbar("招待メールを送信しました", { variant: "success" });
        return result;
      } catch (e) {
        enqueueSnackbar(e?.response?.data?.detail || e?.message, { variant: "error" });
        throw e;
      }
    },
    [coporateApi, isDemo],
  );

  const postResendInvitation = useCallback(
    async (invitationId: string) => {
      if (isDemo) {
        enqueueSnackbar("デモ環境では変更できません", { variant: "info" });
        return;
      }
      try {
        const result = await coporateApi.postResendInvitation(invitationId);
        enqueueSnackbar("招待メールを再送しました", { variant: "success" });
        return result;
      } catch (e) {
        enqueueSnackbar(e?.response?.data?.detail || e?.message, { variant: "error" });
        throw e;
      }
    },
    [coporateApi, isDemo],
  );

  const deleteInvitation = useCallback(
    async (invitationId: string) => {
      if (isDemo) {
        enqueueSnackbar("デモ環境では変更できません", { variant: "info" });
        return;
      }
      try {
        const result = await coporateApi.deleteInvitation(invitationId);
        enqueueSnackbar("招待を取り消しました", { variant: "success" });
        return result;
      } catch (e) {
        enqueueSnackbar(e?.response?.data?.detail || e?.message, { variant: "error" });
        throw e;
      }
    },
    [coporateApi, isDemo],
  );

  const getOrganizationInstagramAccounts = useCallback(async () => {
    if (isDemo) return SAMPLE_CORPORATE_ORG_INSTAGRAM_ACCOUNTS;
    try {
      return await coporateApi.getOrganizationInstagramAccounts();
    } catch (e) {
      enqueueSnackbar(e?.response?.data?.detail || e?.message, { variant: "error" });
      return [];
    }
  }, [coporateApi, isDemo]);

  const getOrganizationInstagramCandidates = useCallback(async () => {
    if (isDemo) return [];
    try {
      return await coporateApi.getOrganizationInstagramCandidates();
    } catch (e) {
      enqueueSnackbar(e?.response?.data?.detail || e?.message, { variant: "error" });
      return [];
    }
  }, [coporateApi, isDemo]);

  const postOrganizationInstagramAccounts = useCallback(
    async (instagramBusinessIds: string[]) => {
      if (isDemo) {
        enqueueSnackbar("デモ環境では変更できません", { variant: "info" });
        return;
      }
      try {
        const result = await coporateApi.postOrganizationInstagramAccounts(instagramBusinessIds);
        enqueueSnackbar("Instagramアカウントを追加しました", { variant: "success" });
        return result;
      } catch (e) {
        enqueueSnackbar(e?.response?.data?.detail || e?.message, { variant: "error" });
        throw e;
      }
    },
    [coporateApi, isDemo],
  );

  const removeOrganizationInstagramAccounts = useCallback(
    async (instagramBusinessIds: string[]) => {
      if (isDemo) {
        enqueueSnackbar("デモ環境では変更できません", { variant: "info" });
        return;
      }
      try {
        const result = await coporateApi.postRemoveOrganizationInstagramAccounts(instagramBusinessIds);
        enqueueSnackbar(`${result.removed}件のInstagramアカウントを組織から外しました`, { variant: "success" });
        return result;
      } catch (e) {
        enqueueSnackbar(e?.response?.data?.detail || e?.message, { variant: "error" });
        throw e;
      }
    },
    [coporateApi, isDemo],
  );

  const getCorporateInstagramTableV2 = useCallback(
    async (
      queryParams: APIOps["get_firestore_instagram_list_api_corporate_organizations__organization_id__instagram_account_v2_list_get"]["parameters"]["query"],
    ) => {
      // デモモードは実データを持たないためサンプルを返す（APIは叩かない）
      if (isDemo) return SAMPLE_CORPORATE_ACCOUNT_LIST_RESPONSE;
      try {
        return await coporateApi.getCorporateInstagramTableV2(queryParams);
      } catch (e) {
        enqueueSnackbar(e?.response?.data?.detail || e?.message, {
          variant: "error",
        });
        throw e;
      }
    },
    [coporateApi, isDemo],
  );

  const getCorporateInstagramSimpleList = useCallback(async () => {
    // デモモードは実データを持たないためサンプルを返す（APIは叩かない）
    if (isDemo) return SAMPLE_CORPORATE_ACCOUNTS;
    try {
      return await coporateApi.getCorporateInstagramSimpleList();
    } catch (e) {
      enqueueSnackbar(e?.response?.data?.detail || e?.message, {
        variant: "error",
      });
    }
  }, [coporateApi, isDemo]);

  const getCorporateInstagramInsightList = useCallback(
    async (
      queryParams: APIOps["get_instagram_table_insight_for_corp_api_corporate_organizations__organization_id__instagram_account_insight_list_post"]["parameters"]["query"],
      requestBody: APIOps["get_instagram_table_insight_for_corp_api_corporate_organizations__organization_id__instagram_account_insight_list_post"]["requestBody"]["content"]["application/json"],
    ) => {
      // デモモードは実データを持たないためサンプルを返す（APIは叩かない）
      if (isDemo) return SAMPLE_CORPORATE_INSIGHT;
      try {
        return await coporateApi.getCorporateInstagramInsightList(queryParams, requestBody);
      } catch (e) {
        enqueueSnackbar(e?.response?.data?.detail || e?.message, {
          variant: "error",
        });
      }
    },
    [coporateApi, isDemo],
  );

  const getCorporateInstagramDetail = useCallback(
    async (instagramAccountId: string) => {
      // デモモードは実データを持たないためサンプルを返す（APIは叩かない）
      if (isDemo) return { user_list: [], benchmark_account_list: [] };
      try {
        return await coporateApi.getCorporateInstagramDetail(instagramAccountId);
      } catch (e) {
        enqueueSnackbar(e?.response?.data?.detail || e?.message, {
          variant: "error",
        });
      }
    },
    [coporateApi, isDemo],
  );

  const postCorporateInstagramUpdateLabel = useCallback(
    async (instagramAccountId: string, labelIds: Array<string>) => {
      if (isDemo) {
        enqueueSnackbar("デモ環境ではラベルを変更できません", { variant: "info" });
        return;
      }
      try {
        return await coporateApi.postCorporateInstagramUpdateLabel(instagramAccountId, labelIds);
      } catch (e) {
        enqueueSnackbar(e?.response?.data?.detail || e?.message, {
          variant: "error",
        });
      }
    },
    [coporateApi, isDemo],
  );

  const exportCorporateInstagramListCsv = useCallback(
    async (
      queryParams: APIOps["export_firestore_instagram_list_csv_api_corporate_organizations__organization_id__instagram_account_v2_list_export_csv_get"]["parameters"]["query"],
    ) => {
      if (isDemo) {
        enqueueSnackbar("デモ環境ではCSVを出力できません", { variant: "info" });
        return;
      }
      try {
        return await coporateApi.exportCorporateInstagramListCsv(queryParams);
      } catch (e) {
        enqueueSnackbar(e?.response?.data?.detail || e?.message, {
          variant: "error",
        });
        throw e; // エラーを再スローして、呼び出し側でcatchできるようにする
      }
    },
    [coporateApi, isDemo],
  );

  const getCorporateFeedListV2 = useCallback(
    async (params: {
      start_date: string;
      end_date: string;
      instagram_business_id?: string;
      keyword?: string;
      label_ids?: string;
    }) => {
      // デモモードは実データを持たないためサンプルを返す（APIは叩かない）
      if (isDemo) return { items: SAMPLE_CORPORATE_FEED_ITEMS };
      try {
        return await coporateApi.getCorporateFeedListV2(params);
      } catch (e) {
        enqueueSnackbar(e?.response?.data?.detail || e?.message, {
          variant: "error",
        });
      }
    },
    [coporateApi, isDemo],
  );

  const getCorporateReelListV2 = useCallback(
    async (params: {
      start_date: string;
      end_date: string;
      instagram_business_id?: string;
      keyword?: string;
      label_ids?: string;
    }) => {
      // デモモードは実データを持たないためサンプルを返す（APIは叩かない）
      if (isDemo) return { items: SAMPLE_CORPORATE_REEL_ITEMS };
      try {
        return await coporateApi.getCorporateReelListV2(params);
      } catch (e) {
        enqueueSnackbar(e?.response?.data?.detail || e?.message, {
          variant: "error",
        });
      }
    },
    [coporateApi, isDemo],
  );

  const getCorporateFeedFirstList = useCallback(
    async (params: {
      start_date: string;
      end_date: string;
      instagram_business_id?: string;
      keyword?: string;
      label_ids?: string;
    }) => {
      // デモモードは実データを持たないためサンプルを返す（APIは叩かない）
      if (isDemo) return { items: SAMPLE_CORPORATE_FEED_ITEMS };
      try {
        return await coporateApi.getCorporateFeedFirstList(params);
      } catch (e) {
        enqueueSnackbar(e?.response?.data?.detail || e?.message, {
          variant: "error",
        });
      }
    },
    [coporateApi, isDemo],
  );

  const getCorporateReelFirstList = useCallback(
    async (params: {
      start_date: string;
      end_date: string;
      instagram_business_id?: string;
      keyword?: string;
      label_ids?: string;
    }) => {
      // デモモードは実データを持たないためサンプルを返す（APIは叩かない）
      if (isDemo) return { items: SAMPLE_CORPORATE_REEL_ITEMS };
      try {
        return await coporateApi.getCorporateReelFirstList(params);
      } catch (e) {
        enqueueSnackbar(e?.response?.data?.detail || e?.message, {
          variant: "error",
        });
      }
    },
    [coporateApi, isDemo],
  );

  const getCorporateStoryListV2 = useCallback(
    async (params: { start_date: string; end_date: string; instagram_business_id?: string; label_ids?: string }) => {
      // デモモードは実データを持たないためサンプルを返す（APIは叩かない）
      if (isDemo) return { items: SAMPLE_CORPORATE_STORY_ITEMS };
      try {
        return await coporateApi.getCorporateStoryListV2(params);
      } catch (e) {
        enqueueSnackbar(e?.response?.data?.detail || e?.message, {
          variant: "error",
        });
      }
    },
    [coporateApi, isDemo],
  );

  const getCorporateTopWorstMedia = useCallback(
    async (params: {
      start_date: string;
      end_date: string;
      limit?: number;
      feed_sort_by?: string;
      reel_sort_by?: string;
      label_ids?: string;
    }) => {
      // デモモードは実データを持たないためサンプルを返す（APIは叩かない）
      if (isDemo) return SAMPLE_CORPORATE_TOP_WORST_MEDIA;
      try {
        return await coporateApi.getCorporateTopWorstMedia(params);
      } catch (e) {
        enqueueSnackbar(e?.response?.data?.detail || e?.message, {
          variant: "error",
        });
      }
    },
    [coporateApi, isDemo],
  );

  /** サマリー用の期間の値。デモモードは null（カードとランキングは一覧の最新値のまま出す） */
  const getCorporatePeriodSummary = useCallback(
    async (params: { start_date: string; end_date: string; label_ids?: string }) => {
      if (isDemo) return null;
      try {
        return await coporateApi.getCorporatePeriodSummary(params);
      } catch (e) {
        enqueueSnackbar(e?.response?.data?.detail || e?.message, {
          variant: "error",
        });
        throw e;
      }
    },
    [coporateApi, isDemo],
  );

  /** レポート: 組織月次レポートの中身。デモモードは実データが無いので null */
  const getOrgMonthlyReport = useCallback(
    async (params: { month: string; label_ids?: string; account_ids?: string }) => {
      if (isDemo) return null;
      try {
        return await coporateApi.getOrgMonthlyReport(params);
      } catch (e) {
        enqueueSnackbar(e?.response?.data?.detail || e?.message, { variant: "error" });
        throw e;
      }
    },
    [coporateApi, isDemo],
  );

  /** レポート: 組織月次レポートの Excel */
  const downloadOrgMonthlyExcel = useCallback(
    async (params: { month: string; label_ids?: string; account_ids?: string }) => {
      try {
        return await coporateApi.downloadOrgMonthlyExcel(params);
      } catch (e) {
        enqueueSnackbar(
          e?.response?.status === 404
            ? "選んだ条件に当てはまるアカウントがありません。"
            : "Excelを作成できませんでした。時間をおいて、もう一度お試しください。",
          { variant: "error" },
        );
        throw e;
      }
    },
    [coporateApi],
  );

  const downloadPostDetailReportExcel = useCallback(
    async (params: { month: string; label_ids?: string; account_ids?: string }) => {
      try {
        return await coporateApi.downloadPostDetailReportExcel(params);
      } catch (e) {
        enqueueSnackbar(
          e?.response?.status === 404
            ? "選んだ条件に当てはまるアカウントがありません。"
            : "Excel を作れませんでした。時間をおいて、もう一度お試しください。",
          { variant: "error" },
        );
        throw e;
      }
    },
    [coporateApi],
  );

  return {
    getCorporatePeriodSummary,
    corporateAuthSync,
    getOrgMonthlyReport,
    downloadOrgMonthlyExcel,
    downloadPostDetailReportExcel,
    applyDemoSession,
    bootstrapCorporateSession,
    postCoroporateLogin,
    corporateLogout,
    getMyOrganizations,
    getOrganizationMembers,
    patchOrganizationMemberRole,
    removeOrganizationMembers,
    postTransferOwner,
    getOrganizationInvitations,
    postOrganizationInvitation,
    postResendInvitation,
    deleteInvitation,
    getOrganizationInstagramAccounts,
    getOrganizationInstagramCandidates,
    postOrganizationInstagramAccounts,
    removeOrganizationInstagramAccounts,
    getCorporateInstagramTableV2,
    getCorporateInstagramSimpleList,
    getCorporateInstagramInsightList,
    getCorporateInstagramDetail,
    postCorporateInstagramUpdateLabel,
    exportCorporateInstagramListCsv,
    getCorporateFeedListV2,
    getCorporateFeedFirstList,
    getCorporateReelListV2,
    getCorporateReelFirstList,
    getCorporateStoryListV2,
    getCorporateTopWorstMedia,
  };
};
